/**
 * pages/BisnisPage.js
 * Bisnis page (Contact model redesign, batch 2 of the Bisnis page work) — lists every `Business`
 * (a real-world firm, ABOVE the Pusat/Cabang list split — see `utils/businesses.js`'s own header),
 * not a third tab next to Pusat/Cabang and not a sidebar entry. Reached only via a header button
 * on `KontakPage` beside "Impor Kontak"; routed the same off-sidebar way ImportKontakWizard is
 * (`App.js`'s own `page` string, not in the `NAV` array).
 *
 * Master/detail, same inline layout KontakPage already established (`.kontak-layout`/
 * `.kontak-list`/`.kontak-detail`, reused verbatim rather than inventing a second list/detail
 * shell) — list on the left, a firm's own detail on the right once selected.
 *
 * **Outstanding display density (explicit product decision, not a default I invented)**: list
 * rows and each member row inside a firm's detail both show a COMPACT figure (a `StatusPill` for
 * direction + the Nett amount); only the firm's own COMBINED total, in the detail pane, gets the
 * full three-line Utang/Piutang/direction-labelled-Nett tile — the exact shape KontakPage's own
 * Bisnis card already uses for a firm total. A total must never stand alone, so every member row
 * still carries its own figure beside the firm total, just not repeated at the same three-line
 * weight everywhere.
 *
 * **`TxnNilaiCell` is deliberately NOT used anywhere on this page.** It renders one live
 * transaction's own total/sisa/progress (it calls `txnStatus`/`txnTotal`/`txnSisa` on a single
 * `txn` internally) — this page never lists individual transactions, only the aggregate Utang/
 * Piutang/Nett figures `outstandingForBusiness`/`outstandingForContact` already compute. A
 * "recent transactions across this firm" list is out of scope for this batch.
 *
 * **The TRANSFER guard in `outstanding()` is unreachable from this page, confirmed by construction
 * (see `utils/piutangUtang.js`'s own header for the guard itself)**: a Business can only ever be
 * linked to a real (non-internal) Contact — `updateContact` refuses every write to an internal
 * contact outright, and `BusinessLinkModal`'s own contact candidates already filter `!c.internal`
 * — and a TRANSFER's own `contactId` is always an internal contact by construction
 * (`App.js`'s `resolveTransferContact`). So `contactsForBusiness`'s result can never include a
 * TRANSFER-bearing contact, and neither `outstandingForBusiness` nor the per-member
 * `outstandingForContact` calls below need an `!internal` guard the way KontakPage's own Bisnis
 * card does (that card is reachable from ANY selected contact, internal or not — this page only
 * ever reaches real contacts via `contactsForBusiness`).
 *
 * **Add and remove live on both screens, deliberately symmetric** — "+ Tambah Anggota" opens
 * `BusinessLinkModal` in `mode="addMember"` (`fixedBusiness={selected}`), "+ Buat Bisnis Baru" in
 * `mode="create"` — the same component Kontak's own "+ Tautkan ke Bisnis" opens in `mode="link"`,
 * see that file's own header for the full three-mode contract. Each member row's "Lepas" button
 * calls the exact same `onUpdate(contactId, {businessId: null})` KontakPage's own Bisnis card
 * already uses — the same write path, not a second one, so the two screens can never disagree
 * about what unlinking means.
 *
 * Orphan businesses (zero members — `businessRefCount === 0`) are listed like any other (member
 * count "0 entri", an all-zero outstanding) and reachable the same way; "+ Tambah Anggota" is
 * what gives them a way to gain members from this page.
 */
import React, { useMemo, useState } from "react";
import { fmtQty } from "../utils/idGenerators";
import { businessRefCount, contactsForBusiness, displayBusiness } from "../utils/businesses";
import { outstandingForBusiness, outstandingForContact } from "../utils/piutangUtang";
import BusinessLinkModal from "../components/BusinessLinkModal";
import { StatusPill } from "../components/TxnBadges";

const CONTACT_LIST_LABEL = { PUSAT: "Pusat", CABANG: "Cabang" };

// Maps `outstanding()`'s own `nettArah` ("PIUTANG"|"UTANG"|"NOL") onto the three status keys
// `StatusPill` actually knows how to render — "NOL" has no pill of its own (nothing owed either
// way reads as settled, the same real-world meaning "LUNAS" already carries elsewhere in this
// app), so it maps there rather than passing an unrecognized key through.
const pillStatusFor = (o) => (o.nettArah === "NOL" ? "LUNAS" : o.nettArah);
const nettLabel = (o) => (o.nettArah === "PIUTANG" ? "Nett Piutang" : o.nettArah === "UTANG" ? "Nett Utang" : "Nett");

export default function BisnisPage({ businesses = [], contacts = [], transactions = [], onOpenContact, onUpdate, onLinkContacts, onDone }) {
  const [q, setQ] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  // null | { mode: "create"|"addMember", fixedBusiness? } — this page never has an anchor
  // `contact` (that's `mode="link"`, Kontak-only) and never preselects anything (see
  // BusinessLinkModal's own header for why `initialSelectedContactIds` is deliberately absent
  // from both of this page's own modes, not merely unset).
  const [linkModal, setLinkModal] = useState(null);
  const [linkError, setLinkError] = useState(null);

  // Built once per data change (never per keystroke/render) — reuses `businessRefCount`/
  // `outstandingForBusiness` directly rather than re-deriving either count or figure.
  const businessInfo = useMemo(() => {
    const map = new Map();
    for (const b of businesses) {
      map.set(b.id, {
        count: businessRefCount({ contacts }, b.id),
        outstanding: outstandingForBusiness(transactions, contacts, b.id),
      });
    }
    return map;
  }, [businesses, contacts, transactions]);

  const filteredBusinesses = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const list = needle
      ? businesses.filter((b) => displayBusiness(b).toLowerCase().includes(needle))
      : businesses;
    return [...list].sort((a, b) => displayBusiness(a).localeCompare(displayBusiness(b)));
  }, [businesses, q]);

  const selected = businesses.find((b) => b.id === selectedId) || null;

  const selectedMembers = useMemo(
    () => (selected ? contactsForBusiness({ contacts }, selected.id) : []),
    [selected, contacts]
  );
  const selectedOutstanding = useMemo(
    () => (selected ? outstandingForBusiness(transactions, contacts, selected.id) : null),
    [selected, transactions, contacts]
  );
  const memberOutstanding = useMemo(
    () => new Map(selectedMembers.map((c) => [c.id, outstandingForContact(transactions, c.id)])),
    [selectedMembers, transactions]
  );

  // No preselection on either mode — both are genuinely empty starts, `mode`/`fixedBusiness` are
  // the only state either one needs (see BusinessLinkModal's own header for why
  // initialSelectedContactIds is deliberately absent from both, not merely unset).
  const openCreate = () => { setLinkError(null); setLinkModal({ mode: "create" }); };
  const openAddMember = () => { setLinkError(null); setLinkModal({ mode: "addMember", fixedBusiness: selected }); };

  const confirmLink = (payload) => {
    const result = onLinkContacts(payload);
    if (result.ok) {
      setLinkModal(null);
      setLinkError(null);
      setSelectedId(result.businessId);
    } else {
      setLinkError(result.error);
    }
  };

  return (
    <div>
      <div className="page-header">
        <div>
          <h1>Bisnis</h1>
          <div className="page-sub">{businesses.length} bisnis</div>
        </div>
        <div style={{ display: "flex", gap: "var(--gap-sm)" }}>
          <button className="btn" onClick={onDone}>← Kembali ke Kontak</button>
          <button className="btn btn-primary" onClick={openCreate}>+ Buat Bisnis Baru</button>
        </div>
      </div>

      <div className="toolbar">
        <input className="form-input grow" placeholder="Cari nama bisnis…"
          value={q} onChange={(e) => setQ(e.target.value)} />
      </div>

      {(() => {
        const listPane = (
          <div className="kontak-list card">
            {filteredBusinesses.length === 0 ? (
              <div className="empty-state">
                {businesses.length === 0 ? "Belum ada bisnis." : "Tidak ada bisnis cocok."}
              </div>
            ) : (
              filteredBusinesses.map((b) => {
                const info = businessInfo.get(b.id);
                return (
                  <div key={b.id}
                    className={"picker__row" + (b.id === selectedId ? " kontak-row--active" : "")}
                    onClick={() => setSelectedId(b.id)}>
                    <div className="picker__name">{displayBusiness(b)}</div>
                    <div className="picker__meta" style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
                      <span>{info.count} entri</span>
                      <StatusPill status={pillStatusFor(info.outstanding)} />
                      <span>Rp {fmtQty(Math.abs(info.outstanding.nett))}</span>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        );

        // No firm selected: full-width list only — no empty detail pane/placeholder, same
        // convention KontakPage's own master/detail already follows.
        if (!selected) return listPane;

        return (
          <div className="kontak-layout">
            {listPane}
            <div className="kontak-detail">
              <button type="button" className="btn" style={{ marginBottom: "var(--gap-lg)" }}
                onClick={() => setSelectedId(null)}>
                ← Kembali
              </button>

              <div className="card">
                <div className="kontak-biz__header">
                  <h2 className="kontak-biz__name">{displayBusiness(selected)}</h2>
                  <button type="button" className="btn btn-sm" onClick={openAddMember}>+ Tambah Anggota</button>
                </div>

                {/* The one place on this page showing the full three-line Utang/Piutang/Nett
                    tile — the firm's own COMBINED total, same shape as KontakPage's Bisnis card. */}
                <div className="kontak-biz__total kontak-biz__total--firm" style={{ marginBottom: "var(--gap-lg)" }}>
                  <div className="kontak-biz__total-label">Outstanding (seluruh bisnis)</div>
                  <div className="kontak-biz__total-lines">
                    <div className="kontak-biz__total-line">Utang: Rp {fmtQty(selectedOutstanding.utang)}</div>
                    <div className="kontak-biz__total-line">Piutang: Rp {fmtQty(selectedOutstanding.piutang)}</div>
                    <div className="kontak-biz__total-value">{nettLabel(selectedOutstanding)}: Rp {fmtQty(Math.abs(selectedOutstanding.nett))}</div>
                  </div>
                </div>

                <div className="kontak-section-label">Anggota ({selectedMembers.length})</div>
                {selectedMembers.length === 0 ? (
                  <div className="empty-state">Belum ada anggota di bisnis ini.</div>
                ) : (
                  <div className="kontak-biz__member-list">
                    {selectedMembers.map((c) => {
                      const o = memberOutstanding.get(c.id);
                      return (
                        <div key={c.id} className="picker__row" onClick={() => onOpenContact(c)}>
                          <div className="picker__name">
                            {c.name}
                            <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>{CONTACT_LIST_LABEL[c.list] || c.list}</span>
                            {c.deactivated && <span className="chip chip--muted" style={{ marginLeft: "var(--gap-sm)" }}>Nonaktif</span>}
                          </div>
                          <div className="picker__meta" style={{ display: "flex", alignItems: "center", gap: "var(--gap-sm)" }}>
                            <StatusPill status={pillStatusFor(o)} />
                            <span>Rp {fmtQty(Math.abs(o.nett))}</span>
                            <button type="button" className="btn btn-ghost btn-sm"
                              onClick={(e) => { e.stopPropagation(); onUpdate(c.id, { businessId: null }); }}>
                              Lepas {c.name} dari bisnis
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })()}

      {linkModal && (
        <BusinessLinkModal
          mode={linkModal.mode}
          fixedBusiness={linkModal.fixedBusiness}
          contacts={contacts}
          businesses={businesses}
          error={linkError}
          onConfirm={confirmLink}
          onClose={() => { setLinkModal(null); setLinkError(null); }}
        />
      )}
    </div>
  );
}
